import { distToSegment2, pointInPoly } from '../../shared/geom.js';
import { hashUnit } from '../../shared/rng.js';
import type { Building, Terrace } from '../../shared/types.js';
import { clearOfTraffic, Frame, standingHeight, type ObjectBuilder, type ObjectContext } from './cityObjects.js';

/**
 * Tables, chairs and parasols outside the cafés, bars and restaurants that put them out.
 *
 * Which ones do is decided on the server (`terracesFor`), because it reads a tag. Where the
 * tables go is decided here, from the building the café is in: along the wall of it nearest
 * the café's own point, on whatever is between that wall and the kerb, in as many rows as
 * fit. A café mapped as a point on the pavement gets its tables around that point instead,
 * in a row along the street.
 *
 * Never in the carriageway. Every chair is tested against the drawn kerb line with most of a
 * metre to spare and against the tarmac the car physics knows about, and a table that fails
 * either is simply not put out. A pedestrian street is not a carriageway, and a market square
 * is not either: those are exactly where most of the tables in an old town are.
 */

const SET_PITCH = 2.3;
/** Out from the wall to each row's tables; further with parasols, which are wider than a table. */
const ROW_DEPTHS = [0.95, 2.4];
const SHADED_ROW_DEPTHS = [1.3, 2.9];
const PARASOL = 1.05;
const CHAIR_OFFSET = 0.62;
/** Clear of the kerb's edge, and of any wall. */
const KERB_CLEARANCE = 0.8;
const WALL_CLEARANCE = 0.3;
/** Further than this from its café's point, a wall is not the café's. */
const MAX_WALL_DISTANCE = 14;

const TOPS = [0x2b2b2b, 0x8a6a4a, 0xe2e0da, 0x5b5f63];
const CHAIRS = [0x9a7650, 0x2a2a2a, 0xe6e4de, 0x3d5a45, 0x6b4a3a];
const PARASOLS = [0xe9e1cf, 0xf0eeea, 0x2f5d3a, 0x6e1f24, 0x1f3352, 0xb3542a, 0xcbb994];
const FENCE = 0x6a4f3a;

interface Spot {
  x: number;
  z: number;
}

export function buildTerraces(out: ObjectBuilder, ctx: ObjectContext, budget: number): void {
  const terraces = ctx.world.objects?.terraces ?? [];
  if (!terraces.length || budget <= 0) return;
  const byId = new Map(ctx.world.buildings.map((b) => [b.id, b]));
  // Nearest the route first, so a tight budget is spent where the race goes.
  const order = terraces
    .map((t) => ({ t, d: ctx.routeDistance2(t.x, t.z) }))
    .sort((p, q) => p.d - q.d || p.t.id - q.t.id);
  const start = out.triangles;
  const placed = new Map<number, Spot[]>();
  for (const { t } of order) {
    if (out.triangles - start >= budget) break;
    const b = t.building >= 0 ? byId.get(t.building) : undefined;
    const site = b ? alongWall(ctx, t, b) : onPavement(ctx, t);
    if (site) terrace(out, ctx, t, site, placed);
  }
}

/** Where a terrace goes: a frame whose `u` runs along the front and whose `-v` points out. */
interface Site {
  frame: Frame;
  /** Along the front, the span tables may use. */
  from: number;
  to: number;
  /** How far out from the front there is room. */
  depth: number;
}

function alongWall(ctx: ObjectContext, t: Terrace, b: Building): Site | null {
  const n = b.poly.length / 2;
  let best: Site | null = null;
  let bestDistance = MAX_WALL_DISTANCE;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = b.poly[i * 2], az = b.poly[i * 2 + 1];
    const bx = b.poly[j * 2], bz = b.poly[j * 2 + 1];
    const len = Math.hypot(bx - ax, bz - az);
    if (len < 2.6) continue;
    const { d2 } = distToSegment2(t.x, t.z, ax, az, bx, bz);
    const d = Math.sqrt(d2);
    if (d >= bestDistance) continue;
    const tx = (bx - ax) / len;
    const tz = (bz - az) / len;
    // The frame's v is (-tz, tx): into the building, for a counter-clockwise ring.
    const frame = new Frame(ax, az, tx, tz, 0);
    const along = Math.max(1.2, Math.min(len - 1.2, (t.x - ax) * tx + (t.z - az) * tz));
    // Out on the street or the square the café fronts. Behind it, in a yard nobody can see
    // from a car, the tables were wasted, so a terrace with no room at the front is not
    // moved round the back.
    if (!facesStreet(ctx, frame, along)) continue;
    const depth = room(ctx, frame, along);
    if (depth < 1.5) continue;
    bestDistance = d;
    best = { frame, from: Math.max(0.4, along - 5), to: Math.min(len - 0.4, along + 5), depth };
  }
  return best;
}

function onPavement(ctx: ObjectContext, t: Terrace): Site | null {
  if (!clearOfTraffic(ctx, t.x, t.z, KERB_CLEARANCE) || ctx.buildings.resolveCircle(t.x, t.z, 0.5)) return null;
  // The kerb's direction, from which way the distance to it grows.
  const e = 0.5;
  const gx = ctx.edges.distance(t.x + e, t.z) - ctx.edges.distance(t.x - e, t.z);
  const gz = ctx.edges.distance(t.x, t.z + e) - ctx.edges.distance(t.x, t.z - e);
  const g = Math.hypot(gx, gz);
  const angle = Number.isFinite(g) && g > 1e-3 ? Math.atan2(gz, gx) : hashUnit(ctx.world.seed, t.id) * Math.PI;
  // u along the kerb, and -v away from the road, so `room` measures across the pavement.
  const ux = -Math.sin(angle);
  const uz = Math.cos(angle);
  const frame = new Frame(t.x, t.z, ux, uz, 0);
  return { frame, from: -3.5, to: 3.5, depth: 3 };
}

/** Whether a wall looks onto a street, a footway or a square within a few metres. */
function facesStreet(ctx: ObjectContext, frame: Frame, along: number): boolean {
  for (const d of [2, 6, 10]) {
    const x = frame.x(along, -d);
    const z = frame.z(along, -d);
    if (ctx.edges.distance(x, z) < 2 || ctx.walkways.distance(x, z) < 2) return true;
    if (ctx.squares.some((a) => pointInPoly(a.poly, x, z))) return true;
  }
  return false;
}

/** How far out from a wall is free: no building, and the kerb a clearance away. */
function room(ctx: ObjectContext, frame: Frame, along: number): number {
  let free = 0;
  for (let d = WALL_CLEARANCE + 0.2; d <= 6; d += 0.25) {
    const x = frame.x(along, -d);
    const z = frame.z(along, -d);
    if (!clearOfTraffic(ctx, x, z, KERB_CLEARANCE) || ctx.buildings.resolveCircle(x, z, 0.25)) break;
    free = d;
  }
  return free;
}

function terrace(out: ObjectBuilder, ctx: ObjectContext, t: Terrace, site: Site, placed: Map<number, Spot[]>): void {
  const look = hashUnit(ctx.world.seed, t.id ^ 0x6d2b79f5);
  const pick = <T,>(list: T[], k: number) => list[Math.floor(((look * k) % 1) * list.length) % list.length];
  const top = pick(TOPS, 3.1);
  const chair = pick(CHAIRS, 5.7);
  const parasol = (look * 11.3) % 1 < 0.6 ? pick(PARASOLS, 7.9) : null;
  const span = Math.min(site.to - site.from, 3 + ((look * 13.7) % 1) * 7);
  const centre = (site.from + site.to) / 2;
  const sets = Math.max(1, Math.floor(span / SET_PITCH));
  const rows = (parasol === null ? ROW_DEPTHS : SHADED_ROW_DEPTHS).filter((d) => d + 0.55 <= site.depth);
  if (!rows.length) return;

  let outer = 0;
  let first = Infinity;
  let last = -Infinity;
  for (const row of rows) {
    for (let k = 0; k < sets; k++) {
      const a = centre + (k - (sets - 1) / 2) * SET_PITCH;
      const x = site.frame.x(a, -row);
      const z = site.frame.z(a, -row);
      if (!fits(ctx, site.frame, a, row) || crowded(placed, x, z)) continue;
      remember(placed, x, z);
      const y = standingHeight(ctx, x, z);
      const f = new Frame(x, z, site.frame.ux, site.frame.uz, y);
      tableSet(out, f, top, chair, parasol);
      outer = Math.max(outer, row);
      first = Math.min(first, a);
      last = Math.max(last, a);
    }
  }
  // A low wooden screen round about half of them: a Polish `ogródek`, a Paris café's glazed
  // barrier. Only where all of it is off the carriageway too.
  if (outer === 0 || (look * 17.3) % 1 > 0.45) return;
  const edge = outer + 0.8;
  const a0 = first - 1.2;
  const a1 = last + 1.2;
  for (const [a, d] of [[a0, edge], [a1, edge], [(a0 + a1) / 2, edge]]) {
    if (!clearOfTraffic(ctx, site.frame.x(a, -d), site.frame.z(a, -d), 0.3)) return;
  }
  const y = standingHeight(ctx, site.frame.x((a0 + a1) / 2, -edge), site.frame.z((a0 + a1) / 2, -edge));
  const f = new Frame(site.frame.ox, site.frame.oz, site.frame.ux, site.frame.uz, y);
  out.paint(FENCE);
  out.box(f, a0, a1, -edge - 0.03, -edge + 0.03, 0, 0.72);
  for (const a of [a0, a1]) out.box(f, a - 0.03, a + 0.03, -edge, -0.25, 0, 0.72);
}

function fits(ctx: ObjectContext, frame: Frame, a: number, row: number): boolean {
  for (const [da, db] of [[0, 0], [-CHAIR_OFFSET - 0.2, 0], [CHAIR_OFFSET + 0.2, 0], [0, -0.45], [0, 0.45]]) {
    const x = frame.x(a + da, -row + db);
    const z = frame.z(a + da, -row + db);
    if (!clearOfTraffic(ctx, x, z, KERB_CLEARANCE)) return false;
    if (ctx.buildings.resolveCircle(x, z, 0.3)) return false;
  }
  return true;
}

/** A table, a chair either side of it facing it, and a parasol over it if there are any. */
function tableSet(out: ObjectBuilder, f: Frame, top: number, chair: number, parasol: number | null): void {
  out.paint(top).box(f, -0.36, 0.36, -0.36, 0.36, 0.71, 0.75, true);
  out.paint(0x2a2a2a).beam(...f.at(0, 0, 0), ...f.at(0, 0, 0.71), 0.06);
  out.paint(chair);
  for (const side of [-1, 1]) {
    const a = side * CHAIR_OFFSET;
    const back = side * (CHAIR_OFFSET + 0.2);
    out.box(f, Math.min(a - side * 0.2, a + side * 0.2), Math.max(a - side * 0.2, a + side * 0.2), -0.21, 0.21, 0.43, 0.47, true);
    out.box(f, Math.min(back, back - side * 0.04), Math.max(back, back - side * 0.04), -0.21, 0.21, 0.47, 0.86);
    // The legs, as the two side frames they are on a bistro chair.
    for (const b of [-0.19, 0.19]) {
      out.sheet([...f.at(a - 0.18, b, 0), ...f.at(a + 0.18, b, 0), ...f.at(a + 0.18, b, 0.43), ...f.at(a - 0.18, b, 0.43)], [f.vx, 0, f.vz]);
    }
  }
  if (parasol === null) return;
  out.paint(0x3a3a3a).beam(...f.at(0, 0, 0.75), ...f.at(0, 0, 2.45), 0.04);
  // A square market parasol: four slopes to a peak, and a valance round the edge.
  const r = PARASOL;
  const peak = f.at(0, 0, 2.5);
  const c = [f.at(-r, -r, 2.15), f.at(r, -r, 2.15), f.at(r, r, 2.15), f.at(-r, r, 2.15)];
  out.paint(parasol);
  for (let i = 0; i < 4; i++) {
    const p = c[i];
    const q = c[(i + 1) % 4];
    const mid = [(p[0] + q[0]) / 2 - f.ox, 0, (p[2] + q[2]) / 2 - f.oz] as [number, number, number];
    out.quad([...p, ...q, ...peak, ...peak], [mid[0], 1, mid[2]]);
    out.quad([...p, ...q, q[0], q[1] - 0.18, q[2], p[0], p[1] - 0.18, p[2]], mid);
  }
  // Its underside, darker, seen from the chase camera's height.
  out.paint(shade(parasol, 0.7)).quad([...c[0], ...c[1], ...c[2], ...c[3]], [0, -1, 0]);
}

function crowded(placed: Map<number, Spot[]>, x: number, z: number): boolean {
  for (let dx = -1; dx <= 1; dx++) {
    for (let dz = -1; dz <= 1; dz++) {
      for (const s of placed.get(key(x, z, dx, dz)) ?? []) {
        if (Math.hypot(s.x - x, s.z - z) < 1.6) return true;
      }
    }
  }
  return false;
}

function remember(placed: Map<number, Spot[]>, x: number, z: number): void {
  const k = key(x, z, 0, 0);
  const list = placed.get(k);
  if (list) list.push({ x, z });
  else placed.set(k, [{ x, z }]);
}

function key(x: number, z: number, dx: number, dz: number): number {
  return (Math.floor(x / 4) + dx) * 100003 + Math.floor(z / 4) + dz;
}

function shade(color: number, k: number): number {
  const ch = (shift: number) => Math.min(255, Math.round(((color >> shift) & 0xff) * k));
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}
